import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, Rocket, Gear, Lightning, Code, ShieldCheck, ChartLineUp, Robot } from '@phosphor-icons/react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const sections = [
  {
    id: 'getting-started',
    icon: Rocket,
    title: 'Getting Started',
    content: [
      { q: 'What is DeFiBot?', a: 'DeFiBot is an AI-powered trading terminal that scans mempools across 7 EVM networks, identifies profitable MEV and arbitrage opportunities, and executes trades automatically in under 200ms.' },
      { q: 'How do I start?', a: '1. Go to the Trading Terminal via "Launch Terminal"\n2. Choose your network (Ethereum, BSC, Base, etc.)\n3. Configure your bot settings or use a preset (Conservative, Balanced, Aggressive)\n4. Connect your wallet and click Start Bot\n5. The AI agent will begin scanning and executing trades' },
      { q: 'Is it free?', a: 'Yes! The Starter plan includes a 7-day free trial with basic arbitrage scanning and 3 trading pairs per network. Upgrade to Professional or Enterprise for unlimited access.' },
    ],
  },
  {
    id: 'trading-modes',
    icon: Robot,
    title: 'Trading Modes',
    content: [
      { q: 'Mixed Mode', a: 'Combines arbitrage and MEV strategies. The AI agent dynamically switches between modes based on market conditions. Recommended for most users. ~5 trades/hour.' },
      { q: 'Arbitrage Only', a: 'Scans for price discrepancies across DEXes (Uniswap, SushiSwap, PancakeSwap, Curve, etc.) and executes cross-DEX trades. Spread range: 0.5-2.5%. ~4 trades/hour.' },
      { q: 'MEV Only', a: 'Monitors the mempool for sandwich opportunities, liquidation events, and frontrunning potential. Higher risk/reward. ~3 trades/hour.' },
      { q: 'Sniper Mode', a: 'Watches for new token listings and executes instant buys on launch. Best for meme coins and new DeFi tokens. ~2 trades/hour.' },
    ],
  },
  {
    id: 'configuration',
    icon: Gear,
    title: 'Configuration',
    content: [
      { q: 'Presets', a: 'Conservative: Low risk, 0.05-0.1 trade size, minimum profit 0.15%\nBalanced: Moderate risk, 0.1-0.2 trade size, minimum profit 0.08%\nAggressive: High risk, 0.5-1.5 trade size, minimum profit 0.03%' },
      { q: 'Trade Size', a: 'Set a MIN and MAX trade size in native tokens (ETH, BNB, etc.). Each trade randomly picks a size within your range for better execution and lower detection.' },
      { q: 'Gas Strategy', a: 'Low: ~60% of market gas, slower but cheaper\nMedium: Market rate, balanced speed/cost\nHigh: 1.8x market gas, fast confirmation\nAggressive: 3x gas, priority execution' },
      { q: 'Advanced Settings', a: 'Take Profit %, Stop Loss %, Min Profit %, Slippage tolerance, Bundle retries, Execution timeout, Mempool depth, Priority multiplier — all configurable for fine-tuned control.' },
    ],
  },
  {
    id: 'networks',
    icon: Lightning,
    title: 'Supported Networks',
    content: [
      { q: 'Ethereum', a: 'The largest DeFi ecosystem. Higher gas costs but the deepest liquidity and most arbitrage opportunities. Best for large trade sizes ($1000+).' },
      { q: 'BNB Chain (BSC)', a: 'Low gas costs (~$0.30). Excellent for smaller trade sizes. PancakeSwap, Biswap, and other DEXes provide ample arbitrage spreads.' },
      { q: 'Base', a: 'Coinbase\'s L2. Very low gas ($0.15). Growing DeFi ecosystem with Aerodrome as the primary DEX. Great for beginners.' },
      { q: 'Arbitrum, Polygon, Optimism, Avalanche', a: 'All supported with optimized gas calculations. The profit calculator automatically recommends the best network based on your trade size and current gas costs.' },
    ],
  },
  {
    id: 'ai-agent',
    icon: ChartLineUp,
    title: 'AI Trading Agent',
    content: [
      { q: 'How does the AI work?', a: 'The AI agent performs: Mempool Analysis (scanning pending transactions), Pattern Detection (identifying profitable patterns), Route Optimization (finding the best DEX path), Risk Assessment, Gas Prediction, Spread Scanning, Liquidity Mapping, and Slippage Calculation.' },
      { q: 'Profit Calculator', a: 'The live profit calculator on the dashboard shows estimated profit Per Trade, Per Hour, Per Day, and Per Week. Values update every 5 seconds based on current market conditions, gas prices, and your settings.' },
      { q: 'Network Recommendation', a: 'The system automatically compares profitability across all 7 networks and suggests the optimal one. Example: "Switch to Base for +$2.40/hr more profit".' },
    ],
  },
  {
    id: 'security',
    icon: ShieldCheck,
    title: 'Security',
    content: [
      { q: 'How are my funds protected?', a: 'DeFiBot never has custody of your funds. All trades are executed through your connected wallet. We use Flashbots private relay to prevent frontrunning of your transactions.' },
      { q: 'Smart contract audits', a: 'All smart contracts are audited by OpenZeppelin and Certik. Bug bounty program active with up to $100K rewards.' },
      { q: 'Data privacy', a: 'We do not store your private keys, seed phrases, or transaction history on our servers. All data is processed locally in your browser.' },
    ],
  },
];

const Docs = () => (
  <div className="min-h-screen pt-24 pb-16" data-testid="docs-page">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-16">
        <motion.span variants={fadeUp} className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">Documentation</motion.span>
        <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-bold text-[var(--text-primary)] mt-4 mb-6" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          DeFiBot Docs
        </motion.h1>
        <motion.p variants={fadeUp} className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
          Everything you need to know about configuring and using the DeFiBot trading terminal.
        </motion.p>
      </motion.div>

      {/* Quick Nav */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
        className="flex flex-wrap justify-center gap-2 mb-16">
        {sections.map((s) => (
          <button key={s.id} onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-sm text-[var(--text-secondary)] hover:text-[var(--color-primary)] hover:border-[var(--color-primary)]/30 transition-all">
            <s.icon size={16} weight="bold" /> {s.title}
          </button>
        ))}
      </motion.div>

      {/* Sections */}
      <div className="space-y-16">
        {sections.map((section, si) => (
          <motion.div key={section.id} id={section.id} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center">
                <section.icon size={20} className="text-[var(--color-primary)]" weight="bold" />
              </div>
              <h2 className="text-2xl font-bold text-[var(--text-primary)]" style={{ fontFamily: "'Unbounded', sans-serif" }}>{section.title}</h2>
            </motion.div>
            <div className="space-y-4">
              {section.content.map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="p-5 rounded-xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
                  <h3 className="text-base font-bold text-[var(--text-primary)] mb-2">{item.q}</h3>
                  <p className="text-sm text-[var(--text-secondary)] whitespace-pre-line leading-relaxed">{item.a}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-16 p-8 rounded-2xl border border-[var(--color-primary)]/20 text-center" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.05), rgba(0,71,255,0.03))' }}>
        <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">Still have questions?</h3>
        <p className="text-[var(--text-secondary)] mb-6">Check our FAQ or contact support.</p>
        <div className="flex justify-center gap-4">
          <Link to="/faq" className="px-6 py-3 rounded-xl bg-[var(--color-primary)] text-[var(--bg-primary)] font-semibold">View FAQ</Link>
          <Link to="/contact" className="px-6 py-3 rounded-xl border border-[var(--border-default)] text-[var(--text-primary)] font-semibold">Contact Us</Link>
        </div>
      </motion.div>
    </div>
  </div>
);

export default Docs;
